import { useState, useEffect, useRef } from "react";
import { DraftVariations } from "../types/creator-studio";
import usePfmPostSocialApi from "../api/use-pfm-post-social.api";
import { BrandDetails } from "../types/add-brand-types";
import { PostContentPayload } from "../types/post-content";
import { deriveTwitterDraft, normalizeSocialDraftText } from "../utils/social-draft";
import { normalizeSocialPlatformIds } from "../utils/platform-post-util";

export interface MediaMetadata {
  url: string;
  width: number;
  height: number;
  size: number;
  aspectRatio: number;
  type: "image" | "video";
  duration?: number;
}

export const usePfmPostSocial = (
  brandDetails: BrandDetails,
  selectedVariation?: DraftVariations,
  historyContent?: PostContentPayload,
  prefilledScheduleDate?: Date,
  prefilledPlatforms?: string[]
) => {
  const normalizedSelectedBody = normalizeSocialDraftText(
    selectedVariation?.body ?? ""
  );
  const [postTitle, setPostTitle] = useState(
    historyContent?.title ?? selectedVariation?.subject ?? ""
  );
  const [postBody, setPostBody] = useState(
    historyContent?.content ?? normalizedSelectedBody
  );

  const [tweetBody, setTweetBody] = useState(
    historyContent?.content ?? deriveTwitterDraft(normalizedSelectedBody)
  );

  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(() => {
    const platforms =
      historyContent?.platforms ??
      prefilledPlatforms ??
      brandDetails.pfm_social_profile_ids?.accounts
        .filter((account) => account.isActive)
        ?.map((account) => account.platform) ??
      [];

    return normalizeSocialPlatformIds(platforms);
  });
  const [scheduleOption, setScheduleOption] = useState<"now" | "later">(
    historyContent?.scheduled || prefilledScheduleDate ? "later" : "now"
  );
  const [scheduledDate, setScheduledDate] = useState<Date | null>(
    historyContent?.scheduledFor
      ? new Date(historyContent.scheduledFor)
      : prefilledScheduleDate ?? new Date()
  );
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState<string[]>([]);
  const [mediaMetadata, setMediaMetadata] = useState<MediaMetadata[]>([]);
  const [useCommonMediaForAllPlatforms, setUseCommonMediaForAllPlatforms] =
    useState<boolean>(historyContent?.useCommonMediaForAllPlatforms ?? true);

  const [threadEnabled, setThreadEnabled] = useState(false);
  const [threadTweets, setThreadTweets] = useState<string[]>([""]);

  const [isStory, setIsStory] = useState(false);

  const [subreddits, setSubreddits] = useState<string[]>(
    historyContent?.subreddits ?? []
  );

  const { createPost, updatePost, uploadMedia, searchSubreddit } =
    usePfmPostSocialApi();

  const urlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const urlsToCleanup = urlsRef.current;
    return () => {
      urlsToCleanup.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      urlsToCleanup.clear();
    };
  }, []);

  const togglePlatform = (id: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleMediaUpload = async (files: File[]) => {
    setMediaFiles((prev) => [...prev, ...files]);

    const metadataPromises = files?.map((file) => {
      return new Promise<MediaMetadata>((resolve) => {
        const url = URL.createObjectURL(file);
        const isVideo = file.type.startsWith("video/");

        if (isVideo) {
          const video = document.createElement("video");
          video.preload = "metadata";

          video.onloadedmetadata = () => {
            resolve({
              url,
              width: video.videoWidth,
              height: video.videoHeight,
              size: file.size,
              aspectRatio: video.videoWidth / video.videoHeight,
              type: "video",
              duration: video.duration,
            });
          };

          video.onerror = () => {
            resolve({
              url,
              width: 0,
              height: 0,
              size: file.size,
              aspectRatio: 0,
              type: "video",
              duration: 0,
            });
          };

          video.src = url;
        } else {
          const img = new Image();

          img.onload = () => {
            resolve({
              url,
              width: img.width,
              height: img.height,
              size: file.size,
              aspectRatio: img.width / img.height,
              type: "image",
            });
          };

          img.onerror = () => {
            resolve({
              url,
              width: 0,
              height: 0,
              size: file.size,
              aspectRatio: 0,
              type: "image",
            });
          };

          img.src = url;
        }
      });
    });

    const newMetadata = await Promise.all(metadataPromises);

    newMetadata.forEach((m) => urlsRef.current.add(m.url));

    setMediaMetadata((prev) => [...prev, ...newMetadata]);
    setMediaPreviewUrls((prev) => [...prev, ...newMetadata.map((m) => m.url)]);
  };

  const removeMedia = (index: number) => {
    const urlToRevoke = mediaPreviewUrls[index];
    if (urlToRevoke) {
      URL.revokeObjectURL(urlToRevoke);
      urlsRef.current.delete(urlToRevoke);
    }

    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    setMediaPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    setMediaMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const reorderMedia = (fromIndex: number, toIndex: number) => {
    setMediaFiles((prev) => {
      const newFiles = [...prev];
      const [removed] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, removed);
      return newFiles;
    });
    setMediaPreviewUrls((prev) => {
      const newUrls = [...prev];
      const [removed] = newUrls.splice(fromIndex, 1);
      newUrls.splice(toIndex, 0, removed);
      return newUrls;
    });
    setMediaMetadata((prev) => {
      const newMetadata = [...prev];
      const [removed] = newMetadata.splice(fromIndex, 1);
      newMetadata.splice(toIndex, 0, removed);
      return newMetadata;
    });
  };

  return {
    postTitle,
    setPostTitle,
    postBody,
    setPostBody,
    tweetBody,
    setTweetBody,
    scheduleOption,
    setScheduleOption,
    scheduledDate,
    setScheduledDate,
    togglePlatform,
    selectedPlatforms,
    mediaFiles,
    mediaPreviewUrls,
    mediaMetadata,
    useCommonMediaForAllPlatforms,
    setUseCommonMediaForAllPlatforms,
    handleMediaUpload,
    removeMedia,
    reorderMedia,
    createPost,
    updatePost,
    uploadMedia,
    threadEnabled,
    setThreadEnabled,
    threadTweets,
    setThreadTweets,
    isStory,
    setIsStory,
    subreddits,
    setSubreddits,
    searchSubreddit,
  };
};
